import { Point, CanvasBounds } from './types'
import { MapElement } from '../../../types'

const isLineBasedElement = (element: MapElement): boolean => {
  return [
    'wall',
    'door',
    'window',
    'line',
    'polygon',
    'room',
    'rectangle',
    'circle',
    'stairs',
    'fixture-rect',
    'fixture-line',
    'fixture-circle',
    'fixture-polygon',
    'measurement',
  ].includes(element.type)
}

const isPointOnLine = (
  point: Point,
  start: Point,
  end: Point,
  tolerance: number,
): boolean => {
  const A = point.x - start.x
  const B = point.y - start.y
  const C = end.x - start.x
  const D = end.y - start.y

  const dot = A * C + B * D
  const lenSq = C * C + D * D

  if (lenSq === 0) return Math.sqrt(A * A + B * B) <= tolerance

  const param = dot / lenSq

  let xx, yy
  if (param < 0) {
    xx = start.x
    yy = start.y
  } else if (param > 1) {
    xx = end.x
    yy = end.y
  } else {
    xx = start.x + param * C
    yy = start.y + param * D
  }

  const dx = point.x - xx
  const dy = point.y - yy
  return Math.sqrt(dx * dx + dy * dy) <= tolerance
}

const isPointInRect = (
  point: Point,
  rectPos: Point,
  width: number,
  height: number,
): boolean => {
  return (
    point.x >= rectPos.x &&
    point.x <= rectPos.x + width &&
    point.y >= rectPos.y &&
    point.y <= rectPos.y + height
  )
}

const isPointInPolygon = (point: Point, vertices: Point[]): boolean => {
  let inside = false
  const x = point.x
  const y = point.y

  for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
    const xi = vertices[i].x
    const yi = vertices[i].y
    const xj = vertices[j].x
    const yj = vertices[j].y

    const yiAbovePoint = yi > y
    const yjAbovePoint = yj > y
    const crossesRay = yiAbovePoint !== yjAbovePoint
    const intersectionX = ((xj - xi) * (y - yi)) / (yj - yi) + xi

    if (crossesRay && x < intersectionX) {
      inside = !inside
    }
  }

  return inside
}

const calculatePolygonArea = (vertices: Point[]): number => {
  if (vertices.length < 3) return 0

  let area = 0
  for (let i = 0; i < vertices.length; i++) {
    const j = (i + 1) % vertices.length
    area += vertices[i].x * vertices[j].y
    area -= vertices[j].x * vertices[i].y
  }
  return Math.abs(area) / 2
}

// Calculate area for a polygon with curved edges using Bézier curve integration
const calculateCurvedPolygonArea = (
  vertices: Point[],
  curvedEdges: {
    edgeIndex: number
    controlPoint1: Point
    controlPoint2: Point
    curvature: number
  }[],
): number => {
  if (vertices.length < 3) return 0

  let area = 0

  // Create a map for quick lookup of curved edges
  const curvedEdgeMap = new Map()
  curvedEdges.forEach(edge => {
    curvedEdgeMap.set(edge.edgeIndex, edge)
  })

  for (let i = 0; i < vertices.length; i++) {
    const j = (i + 1) % vertices.length
    const startVertex = vertices[i]
    const endVertex = vertices[j]

    if (curvedEdgeMap.has(i)) {
      // This edge is curved - calculate area under Bézier curve
      const curvedEdge = curvedEdgeMap.get(i)
      const cp1 = curvedEdge.controlPoint1
      const cp2 = curvedEdge.controlPoint2

      // Use numerical integration to approximate the area under the curve
      // We'll sample the curve at multiple points to get a good approximation
      const samples = 20 // Number of sample points along the curve
      let curveArea = 0

      // Calculate area using shoelace formula for the curve segments
      let prevPoint = startVertex
      for (let t = 1; t <= samples; t++) {
        const tNorm = t / samples
        const curPoint = getBezierPoint(startVertex, cp1, cp2, endVertex, tNorm)

        // Add the area contribution using shoelace formula
        curveArea += prevPoint.x * curPoint.y - curPoint.x * prevPoint.y
        prevPoint = curPoint
      }

      area += curveArea
    } else {
      // Regular straight edge - use standard shoelace formula
      area += startVertex.x * endVertex.y
      area -= endVertex.x * startVertex.y
    }
  }

  return Math.abs(area) / 2
}

// Helper function to calculate a point on a Bézier curve at parameter t (0 to 1)
const getBezierPoint = (
  p0: Point,
  p1: Point,
  p2: Point,
  p3: Point,
  t: number,
): Point => {
  const u = 1 - t
  const tt = t * t
  const uu = u * u
  const uuu = uu * u
  const ttt = tt * t

  return {
    x: uuu * p0.x + 3 * uu * t * p1.x + 3 * u * tt * p2.x + ttt * p3.x,
    y: uuu * p0.y + 3 * uu * t * p1.y + 3 * u * tt * p2.y + ttt * p3.y,
  }
}

const getElementBounds = (element: MapElement): CanvasBounds | null => {
  switch (element.type) {
    case 'wall':
    case 'door':
    case 'window':
    case 'line':
      if (element.properties.endPosition) {
        const minX = Math.min(
          element.position.x,
          element.properties.endPosition.x,
        )
        const minY = Math.min(
          element.position.y,
          element.properties.endPosition.y,
        )
        const maxX = Math.max(
          element.position.x,
          element.properties.endPosition.x,
        )
        const maxY = Math.max(
          element.position.y,
          element.properties.endPosition.y,
        )
        return {
          x: minX,
          y: minY,
          width: maxX - minX || 10,
          height: maxY - minY || 10,
        }
      }
      break
    case 'room':
    case 'stairs':
    case 'rectangle':
      if (element.properties.width && element.properties.height) {
        return {
          x: element.position.x,
          y: element.position.y,
          width: element.properties.width,
          height: element.properties.height,
        }
      }
      break
    case 'circle':
      if (element.properties.radius) {
        return {
          x: element.position.x,
          y: element.position.y,
          width: element.properties.radius * 2,
          height: element.properties.radius * 2,
        }
      }
      break
    case 'text':
      const textWidth =
        (element.properties.text || '').length *
        (element.properties.fontSize || 16) *
        0.6
      const textHeight = element.properties.fontSize || 16
      return {
        x: element.position.x,
        y: element.position.y - textHeight,
        width: textWidth,
        height: textHeight,
      }
    case 'poi':
      return {
        x: element.position.x - 15,
        y: element.position.y - 15,
        width: 30,
        height: 30,
      }
    case 'polygon':
      if (
        element.properties.vertices &&
        element.properties.vertices.length > 0
      ) {
        const vertices = element.properties.vertices as Point[]
        let minX = vertices[0].x,
          maxX = vertices[0].x
        let minY = vertices[0].y,
          maxY = vertices[0].y

        vertices.forEach(vertex => {
          minX = Math.min(minX, vertex.x)
          maxX = Math.max(maxX, vertex.x)
          minY = Math.min(minY, vertex.y)
          maxY = Math.max(maxY, vertex.y)
        })

        return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
      }
      break
  }
  return null
}

export {
  isLineBasedElement,
  isPointOnLine,
  isPointInRect,
  isPointInPolygon,
  calculatePolygonArea,
  calculateCurvedPolygonArea,
  getElementBounds,
}
